import { formatDate } from '../../components/data-grid/format';
import { useI18n } from '../../i18n/I18nProvider';
import {
  NOTIFICATION_KINDS,
  filterByKind,
  targetPath,
  unreadCount,
} from './notifications.api';
import type { NotificationKind, NotificationRecord } from './notifications.api';
import './notifications.css';

export interface NotificationsPanelProps {
  records: readonly NotificationRecord[];
  activeKind: NotificationKind | null;
  onSelectKind(kind: NotificationKind | null): void;
  onNavigate(record: NotificationRecord): void;
  onMarkRead(id: string): void;
  onMarkAllRead(): void;
  onClose(): void;
}

function messageOf(record: NotificationRecord): string {
  const message = record.payload.message;
  return typeof message === 'string' ? message : record.entity;
}

export function NotificationsPanel({
  records,
  activeKind,
  onSelectKind,
  onNavigate,
  onMarkRead,
  onMarkAllRead,
  onClose,
}: NotificationsPanelProps): JSX.Element {
  const { t } = useI18n();
  const unread = unreadCount(records);
  const visible = filterByKind(records, activeKind);

  return (
    <section className="notifications" data-testid="notifications-panel">
      <header className="notifications__head">
        <h2>{t('notificationsTitle')}</h2>
        {unread === 0 ? null : (
          <span className="notifications__badge" data-testid="notifications-unread">
            {unread}
          </span>
        )}
        <button type="button" className="btn btn--ghost" data-testid="notifications-mark-all" onClick={onMarkAllRead}>
          {t('notificationsMarkAll')}
        </button>
        <button
          type="button"
          className="notifications__close"
          aria-label={t('notificationsClose')}
          onClick={onClose}
        >
          ×
        </button>
      </header>

      <nav className="notifications__kinds" aria-label={t('notificationsTitle')}>
        <button
          type="button"
          className={`notifications__kind${activeKind === null ? ' is-active' : ''}`}
          onClick={() => onSelectKind(null)}
        >
          {t('notificationsAll')}
        </button>
        {NOTIFICATION_KINDS.map((kind) => (
          <button
            key={kind}
            type="button"
            className={`notifications__kind notifications__kind--${kind}${activeKind === kind ? ' is-active' : ''}`}
            onClick={() => onSelectKind(kind)}
          >
            {t(`notificationsKind_${kind}`)}
          </button>
        ))}
      </nav>

      <ul className="notifications__list">
        {visible.length === 0 ? (
          <li className="notifications__empty">{t('notificationsEmpty')}</li>
        ) : (
          visible.map((record) => {
            const path = targetPath(record);
            return (
              <li
                key={record.id}
                className={`notifications__item notifications__item--${record.kind}${record.readAt === null ? ' is-unread' : ''}`}
              >
                <div className="notifications__row">
                  <strong>{t(`notificationsKind_${record.kind}`)}</strong>
                  <span className="notifications__time">{formatDate(record.createdAt)}</span>
                </div>
                <p className="notifications__message">{messageOf(record)}</p>
                {path === null ? null : (
                  <a
                    className="report__link"
                    href={path}
                    data-testid={`notification-${record.id}`}
                    onClick={(event) => {
                      event.preventDefault();
                      onMarkRead(record.id);
                      onNavigate(record);
                    }}
                  >
                    {t('notificationsGo')}
                  </a>
                )}
              </li>
            );
          })
        )}
      </ul>

      <footer className="notifications__foot">
        <span className="muted">
          {t('notificationsLast24h')}: {records.length}
        </span>
      </footer>
    </section>
  );
}
